import { useState } from "react";
import { Download } from "lucide-react";
import { readJwt } from "../../auth/storage";
import { Button } from "@/components/ui/button";

const DATE_INPUT = "field";

type ExportTable = "cycles" | "decisions";

interface ExportError {
  message: string;
}

/**
 * SPEC §14 Analytics §7 — CSV download UI.
 *
 * <p>CSV only. The Parquet path is the MCP {@code export_to_parquet} tool
 * (SPEC §16), not a backend HTTP endpoint — so there is no Parquet button
 * here, not even a disabled one (a control that can never become active is
 * clutter, not honesty).
 *
 * <p>No source selector (S56c): exports are scoped to the running environment
 * by the backend (S56a). A deployment only holds its own environment's data, so
 * a DEMO/LIVE chooser would be misleading — the request sends no {@code ?source=}
 * and the backend defaults to this environment.
 *
 * <p>Downloads are JS-driven (fetch + blob) so the JWT can travel in
 * the {@code Authorization} header. A plain {@code <a href>} link would
 * lose the bearer token because browsers don't forward localStorage
 * data on cross-origin or in-page anchor clicks.
 */
export default function DataExport() {
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [downloading, setDownloading] = useState<ExportTable | null>(null);
  const [error, setError] = useState<ExportError | null>(null);

  const download = async (table: ExportTable) => {
    setError(null);
    setDownloading(table);
    try {
      const url = buildUrl(table, startDate, endDate);
      const res = await fetch(url, {
        headers: authHeaders(),
      });
      if (res.status === 401) {
        // The shared client.ts handles 401 redirect; mimic it explicitly
        // here since this fetch goes around it.
        window.location.href = "/login";
        return;
      }
      if (!res.ok) {
        const message = await readErrorMessage(res);
        setError({ message });
        return;
      }
      const blob = await res.blob();
      saveBlob(blob, filenameFromResponse(res, table));
    } catch (e) {
      setError({ message: (e as Error).message ?? "Téléchargement échoué" });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="data-export flex flex-col gap-3" data-testid="data-export">
      <div className="flex flex-wrap items-center gap-4">
        <label className="text-muted-foreground flex items-center gap-2 text-sm">
          Du
          <input
            type="date"
            className={DATE_INPUT}
            data-testid="export-start"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
        <label className="text-muted-foreground flex items-center gap-2 text-sm">
          Au
          <input
            type="date"
            className={DATE_INPUT}
            data-testid="export-end"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          data-testid="export-cycles-csv"
          disabled={downloading != null}
          onClick={() => download("cycles")}
        >
          <Download className="size-3.5" aria-hidden />
          {downloading === "cycles" ? "Téléchargement…" : "Télécharger les cycles (CSV)"}
        </Button>
        <Button
          type="button"
          size="sm"
          data-testid="export-decisions-csv"
          disabled={downloading != null}
          onClick={() => download("decisions")}
        >
          <Download className="size-3.5" aria-hidden />
          {downloading === "decisions" ? "Téléchargement…" : "Télécharger les décisions (CSV)"}
        </Button>
      </div>

      {error && (
        <div className="text-loss text-sm" data-testid="export-error" role="alert">
          <strong>Export échoué.</strong> <span>{error.message}</span>
        </div>
      )}
    </div>
  );
}

function buildUrl(
  table: ExportTable,
  startDate: string,
  endDate: string,
): string {
  // No `source` param — the backend scopes the export to the running
  // environment (S56a / S56c). Date filters are still optional.
  const qs = new URLSearchParams();
  qs.set("format", "csv");
  if (startDate) qs.set("startDate", `${startDate}T00:00:00Z`);
  if (endDate) qs.set("endDate", `${endDate}T23:59:59Z`);
  return `/api/analytics/export/${table}?${qs}`;
}

function authHeaders(): Record<string, string> {
  const jwt = readJwt();
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

async function readErrorMessage(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body === "object" && body && "message" in body) {
      return String((body as { message: unknown }).message);
    }
    if (typeof body === "object" && body && "error" in body) {
      return String((body as { error: unknown }).error);
    }
  } catch {
    /* fall through */
  }
  return `HTTP ${res.status}`;
}

function filenameFromResponse(res: Response, fallback: ExportTable): string {
  const cd = res.headers.get("content-disposition");
  if (cd) {
    const match = cd.match(/filename="([^"]+)"/);
    if (match) return match[1];
  }
  return `${fallback}.csv`;
}

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
